import React, { ReactNode } from "react";
import { Navigate, useLocation, Outlet, NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { isAdmin, RoleName } from "../common/enum/Role";

// Props interface for AuthLayout
interface AuthLayoutProps {
  children?: ReactNode;
  fallbackPath?: string; // Where to redirect if not authenticated
  showLoader?: boolean; // Show loading spinner while checking auth
}

// Loading spinner component
const LoadingSpinner: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      <p className="text-gray-600">Checking authentication...</p>
    </div>
  </div>
);

// Header tabs visible to each role.
const navItemsFor = (role?: string) => {
  if (isAdmin(role)) {
    return [
      { to: "/dashboard", label: "Users" },
      { to: "/classes", label: "Lớp học" },
      { to: "/schools", label: "Trường học" },
    ];
  }
  if (role === RoleName.SCHOOL) {
    return [
      { to: "/classes", label: "Lớp học" },
      { to: "/teachers", label: "Giáo viên" },
    ];
  }
  return [{ to: "/classes", label: "Lớp học" }];
};

// Header component for authenticated layout
const AuthHeader: React.FC = () => {
  const { user, logout } = useAuth();

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <nav className="flex items-center space-x-1">
            {navItemsFor(user?.role).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-blue-100 text-blue-700"
                      : "text-gray-700 hover:bg-gray-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">Welcome, {user?.name}</span>
            <button
              onClick={logout}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

// Main AuthLayout component
const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  fallbackPath = "/login",
  showLoader = true,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading && showLoader) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Store the attempted location for redirect after login
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Render authenticated layout
  return (
    <div className="min-h-screen bg-gray-50">
      <AuthHeader />

      <div className="flex">
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">{children || <Outlet />}</div>
        </main>
      </div>
    </div>
  );
};

export default AuthLayout;
